/* eslint-disable no-console */
import { get, flatten } from 'lodash';
import {
 all, call, put, select,
} from 'redux-saga/effects';
import queryString from 'query-string';
import { makeFilterString } from '../../filters';
import { getBbox } from './get-bbox';
import { fetchGraphs } from './fetch-graphs';
import { API, getQueryString } from '../../api';
import { fetchCensusTable } from './fetch-census-table';
import { makeLayerCache } from './make-layer-cache';

export const getServerConfig = ({ serverConfig }) => serverConfig;
export const getFilters = ({ filters }) => filters;
export const getReportResponses = ({ reportResponses }) => reportResponses;

function* fetchAggregate({ serverConfig, filterString, property }) {
  const {
    method,
    source,
    column,
    filterString: propertyFilterString,
  } = property;

  const [cleanedColumn] = column.split('+');
  try {
    const path = `/math/${
      serverConfig.namespace
    }/${method}?${queryString.stringify({
      key: source ? `${source}:${cleanedColumn}` : cleanedColumn,
    })}${filterString ? `&${filterString}` : ''}${
      propertyFilterString ? `&${propertyFilterString}` : ''
    }`;

    const result = yield call(API, 'get', 'api', path);

    if (source) {
      return {
        [`${method}:${source}:${column}`]: result.result,
      };
    }
    return {
      [`${method}:${column}`]: result.result,
    };
  } catch (e) {
    console.error('Error fetching bbox for multiparcel report', e);
    return null;
  }
}

function* fetchAggregates({ serverConfig, filterString, report }) {
  const calls = report.sections.reduce((acc, section) => {
    const properties = section.properties.filter(
      (property) => !!property.method,
    );

    properties.forEach((property) => {
      acc.push(call(fetchAggregate, { serverConfig, filterString, property }));
    });

    return acc;
  }, []);

  const results = yield all(calls);

  const data = (results || []).reduce(
    (acc, aggregate) => ({
      ...acc,
      ...aggregate,
    }),
    {},
  );

  return data;
}

export function* fetchMultiParcel(action) {
  try {
    yield put({ type: 'START_REPORT_LOADING' });
    const { layer, report } = get(action, 'payload.config', {});
    yield put({
      type: 'SELECT_REPORT',
      payload: layer.key,
    });
    yield put({
      type: 'TRACK',
      payload: {
        name: 'Report opened',
        type: 'Multiparcel',
        label: layer.label,
        filters: layer.filters,
      },
    });

    const filters = yield select(getFilters);
    const querystring = yield select(getQueryString);
    const filterString = makeFilterString(
      layer.filters,
      (filters[0].type === 'PARENT')
      ? filters.find(({ source }) => source === layer.source).filters
      : filters,
      querystring,
    );
    const serverConfig = yield select(getServerConfig);
    const reportResponses = yield select(getReportResponses);

    if (reportResponses[layer.key]) {
      yield put({
        type: 'UPDATE_REPORT_CONTENT',
        payload: reportResponses[layer.key],
      });
      yield put({ type: 'STOP_REPORT_LOADING' });
    }

    // create cache
    yield call(makeLayerCache, { serverConfig, filterString, key: layer.key });
    const {
      bboxResult,
      graphs,
      aggregates,
      censusTables,
    } = yield all({
      bboxResult: call(getBbox, { serverConfig, filterString }),
      graphs: call(fetchGraphs, {
        serverConfig,
        filterString,
        report,
        key: layer.key,
      }),
      aggregates: call(fetchAggregates, { serverConfig, filterString, report }),
      censusTables: call(fetchCensusTable, {
        filterString,
        report,
      }),
    });

    yield put({
      type: 'UPDATE_REPORT_CONTENT',
      payload: {
        key: layer.key,
        parcels: [],
        data: aggregates,
        report,
        bbox:
          bboxResult && bboxResult.bboxes.length
            ? bboxResult.bboxes[0].map(([ln, la]) => [
                parseFloat(ln),
                parseFloat(la),
              ])
            : undefined,
        graphs: flatten(graphs)
          .filter(Boolean)
          .reduce((acc, graph) => {
            acc[graph.name] = graph;
            return acc;
          }, {}),
        filterString,
        layer,
        style: layer.style,
        censusTables,
      },
    });

    yield put({ type: 'STOP_REPORT_LOADING' });
  } catch (e) {
    console.error(e);
    yield put({ type: 'STOP_REPORT_LOADING' });
  }
}
